import { useField } from 'formik';
import { Space } from 'antd';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import TruckIconOutlined from 'components/icons/TruckIconOutlined';
import FormSectionHeader from 'components/new-ui/base/form/FormSectionHeader';
import ReadonlyFormItem from 'components/new-ui/base/form/ReadonlyFormItem';
import { CreateJobFormFields } from 'views/jobs/validationSchemas';
import { CreateJobFormSteps } from 'views/jobs/types';
import FormSelect from 'components/new-ui/composed/formSelect/FormSelect';
import useCodelists from 'hooks/useCodelists';
import { useEffect, useMemo } from 'react';
import { DeclarationTypes } from 'store/declarations/enums/common/declaration-types';

const fieldName = 'deliveryTerms.incotermCode';

const DeliveryTermsSection = () => {
    const [field, meta, helpers] = useField(fieldName);
    const { currentStepConfig, isIdle, isComplete, isActive, resetStep } =
        useMultiStepFormContext<CreateJobFormFields>();
    const { aisCodelists, getIrelandCodelists } = useCodelists();

    const stepKey = CreateJobFormSteps.DELIVERY_TERMS;
    const disabled = currentStepConfig?.disabled || false;
    const optional = currentStepConfig?.optional || false;

    const sectionTitle = optional ? 'Delivery Terms (Optional)' : 'Delivery Terms';

    const handleReset = () => {
        resetStep(CreateJobFormSteps.DELIVERY_TERMS, 'incotermCode');
    };

    const selectedOption = useMemo(
        () =>
            aisCodelists?.incotermCode?.find((opt) => {
                return opt.id === field.value;
            }),
        [aisCodelists?.incotermCode, field.value]
    );

    const readonlyLabel = selectedOption?.value ?? field.value;

    useEffect(() => {
        getIrelandCodelists(DeclarationTypes.AIS);
    }, []);

    if (isIdle(stepKey)) {
        return <FormSectionHeader title={sectionTitle} icon={<TruckIconOutlined />} />;
    }

    if (isActive(stepKey)) {
        return (
            <Space style={{ width: '100%' }} direction="vertical" size={16}>
                <FormSectionHeader title={sectionTitle} icon={<TruckIconOutlined />} />
                <FormSelect
                    style={{ minWidth: '100%' }}
                    placeholder="Select delivery term"
                    fieldMeta={meta}
                    selectOptions={aisCodelists?.incotermCode}
                    fieldProps={field}
                    fieldHelper={helpers}
                />
            </Space>
        );
    }

    if (isComplete(stepKey)) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <FormSectionHeader title={sectionTitle} icon={<TruckIconOutlined />} />
                <ReadonlyFormItem disabled={disabled} onReset={handleReset} value={readonlyLabel} />
            </div>
        );
    }

    return null;
};

export default DeliveryTermsSection;
